if (!customElements.get('sooler-ugc-carousel')) {
  customElements.define('sooler-ugc-carousel', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.viewport = this.querySelector('[data-sooler-ugc-viewport]');
      this.carousel = this.querySelector('.sooler-ugc__carousel');
      this.track = this.querySelector('.sooler-ugc__track');
      this.cards = Array.from(this.querySelectorAll('[data-sooler-ugc-card]'));
      this.videoCards = Array.from(this.querySelectorAll('[data-sooler-video-card]'));
      this.previousButton = this.querySelector('[data-sooler-ugc-prev]');
      this.nextButton = this.querySelector('[data-sooler-ugc-next]');
      this.viewer = this.querySelector('[data-sooler-video-viewer]');
      this.viewerFeed = this.querySelector('[data-sooler-viewer-feed]');
      this.viewerClose = this.querySelector('[data-sooler-viewer-close]');
      this.viewerPrevious = this.querySelector('[data-sooler-viewer-prev]');
      this.viewerNext = this.querySelector('[data-sooler-viewer-next]');
      this.viewerPlay = this.querySelector('[data-sooler-viewer-play]');
      this.viewerSound = this.querySelector('[data-sooler-viewer-sound]');
      this.activeVideoIndex = 0;
      this.viewerBuilt = false;
      this.viewerMuted = true;
      this.reviewCards = Array.from(this.querySelectorAll('.sooler-ugc__card--review'));

      this.onScroll = this.updateButtons.bind(this);
      this.onPrevious = () => this.scrollByCard(-1);
      this.onNext = () => this.scrollByCard(1);
      this.onBlockSelect = this.handleBlockSelect.bind(this);
      this.onKeydown = this.handleKeydown.bind(this);
      this.onViewportResize = this.updateOverflowViewport.bind(this);

      this.previousButton?.addEventListener('click', this.onPrevious);
      this.nextButton?.addEventListener('click', this.onNext);
      this.viewport?.addEventListener('scroll', this.onScroll, { passive: true });
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('keydown', this.onKeydown);
      this.initializeVideoCards();
      this.initializeViewer();
      this.initializeReviewClamps();
      this.initializeOverflowViewport();
      this.updateButtons();
    }

    disconnectedCallback() {
      this.previousButton?.removeEventListener('click', this.onPrevious);
      this.nextButton?.removeEventListener('click', this.onNext);
      this.viewport?.removeEventListener('scroll', this.onScroll);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('keydown', this.onKeydown);
      this.reviewResizeObserver?.disconnect();
      this.overflowResizeObserver?.disconnect();
      window.removeEventListener('resize', this.onViewportResize);
      if (this.viewer && this.viewer.parentElement === document.body) this.appendChild(this.viewer);
      this.initialized = false;
    }

    initializeReviewClamps() {
      if (!this.reviewCards.length) return;
      this.updateReviewClamps = this.clampReviewText.bind(this);
      this.reviewResizeObserver = new ResizeObserver(this.updateReviewClamps);
      this.reviewCards.forEach((card) => this.reviewResizeObserver.observe(card));
      requestAnimationFrame(this.updateReviewClamps);
      document.fonts?.ready?.then(this.updateReviewClamps);
    }

    clampReviewText() {
      this.reviewCards.forEach((card) => {
        const quote = card.querySelector('[data-sooler-review-text]');
        const firstParagraph = quote?.firstElementChild || quote;
        if (!quote || !firstParagraph) return;
        const availableHeight = quote.clientHeight;
        const lineHeight = Number.parseFloat(getComputedStyle(firstParagraph).lineHeight) || 20;
        const lines = Math.max(1, Math.floor(availableHeight / lineHeight));
        quote.style.setProperty('--sooler-ugc-review-lines', lines);
      });
    }

    initializeOverflowViewport() {
      if (!this.classList.contains('sooler-ugc--show-overflow') || !this.viewport || !this.carousel || !this.track) return;
      if ('ResizeObserver' in window) {
        this.overflowResizeObserver = new ResizeObserver(this.onViewportResize);
        this.overflowResizeObserver.observe(this.carousel);
      }
      window.addEventListener('resize', this.onViewportResize, { passive: true });
      requestAnimationFrame(this.onViewportResize);
    }

    updateOverflowViewport() {
      if (!this.classList.contains('sooler-ugc--show-overflow') || !this.viewport || !this.carousel || !this.track) return;
      const baseWidth = this.carousel.clientWidth;
      const viewportLeft = this.viewport.getBoundingClientRect().left;
      const availableWidth = Math.max(baseWidth, document.documentElement.clientWidth - viewportLeft);
      this.track.style.width = `${baseWidth}px`;
      this.viewport.style.width = `${availableWidth}px`;
      this.updateButtons();
    }

    initializeVideoCards() {
      this.videoCards.forEach((card, index) => {
        const video = card.querySelector('video');
        const openButton = card.querySelector('[data-sooler-video-open]');
        const soundButton = card.querySelector('[data-sooler-video-sound]');
        if (!video) return;

        const open = (event) => {
          if (event) event.stopPropagation();
          this.openViewer(index);
        };
        card.addEventListener('click', (event) => {
          if (event.target.closest('[data-sooler-video-sound]')) return;
          open(event);
        });
        card.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            open(event);
          }
        });
        openButton?.addEventListener('click', open);
        soundButton?.addEventListener('click', (event) => {
          event.stopPropagation();
          video.muted = !video.muted;
          soundButton.classList.toggle('is-unmuted', !video.muted);
          soundButton.setAttribute('aria-label', video.muted ? 'Turn sound on' : 'Turn sound off');
        });
      });
    }

    initializeViewer() {
      if (!this.viewer) return;
      this.viewerClose?.addEventListener('click', () => this.closeViewer());
      this.viewerPrevious?.addEventListener('click', () => this.activateViewerVideo(this.activeVideoIndex - 1));
      this.viewerNext?.addEventListener('click', () => this.activateViewerVideo(this.activeVideoIndex + 1));
      this.viewerPlay?.addEventListener('click', () => this.toggleViewerPlayback());
      this.viewerSound?.addEventListener('click', () => this.toggleViewerSound());
      this.viewer.addEventListener('click', (event) => {
        if (event.target === this.viewer) this.closeViewer();
      });
    }

    buildViewer() {
      if (this.viewerBuilt || !this.viewerFeed) return;
      this.videoCards.forEach((card, index) => {
        const sourceVideo = card.querySelector('video');
        if (!sourceVideo) return;
        const slide = document.createElement('div');
        const video = sourceVideo.cloneNode(true);
        video.classList.remove('sooler-ugc__video');
        slide.className = 'sooler-ugc__viewer-slide';
        slide.dataset.viewerIndex = index;
        video.removeAttribute('autoplay');
        video.autoplay = false;
        video.controls = false;
        video.loop = true;
        video.muted = true;
        video.preload = 'metadata';
        slide.appendChild(video);
        slide.addEventListener('click', () => this.toggleViewerPlayback());
        this.viewerFeed.appendChild(slide);
      });
      this.viewerBuilt = true;
    }

    viewerSlides() {
      return Array.from(this.viewerFeed?.querySelectorAll('.sooler-ugc__viewer-slide') || []);
    }

    activeViewerVideo() {
      return this.viewerSlides()[this.activeVideoIndex]?.querySelector('video') || null;
    }

    openViewer(index) {
      if (!this.viewer || !this.videoCards.length) return;
      this.buildViewer();
      this.lastFocusedElement = document.activeElement;
      this.previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      this.viewer.hidden = false;
      this.viewer.setAttribute('aria-hidden', 'false');
      document.body.appendChild(this.viewer);
      this.activateViewerVideo(index);
      this.viewerClose?.focus();
    }

    closeViewer() {
      if (!this.viewer || this.viewer.hidden) return;
      this.viewerSlides().forEach((slide) => slide.querySelector('video')?.pause());
      this.viewer.hidden = true;
      this.viewer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = this.previousBodyOverflow || '';
      this.appendChild(this.viewer);
      this.lastFocusedElement?.focus?.();
    }

    activateViewerVideo(index) {
      const slides = this.viewerSlides();
      if (!slides.length) return;
      this.activeVideoIndex = Math.max(0, Math.min(index, slides.length - 1));
      slides.forEach((slide, slideIndex) => {
        const video = slide.querySelector('video');
        const active = slideIndex === this.activeVideoIndex;
        slide.classList.toggle('is-active', active);
        if (!active && video) {
          video.pause();
          video.currentTime = 0;
        }
      });
      const activeVideo = this.activeViewerVideo();
      if (activeVideo) {
        activeVideo.muted = this.viewerMuted;
        const promise = activeVideo.play();
        promise?.catch(() => this.updateViewerControls());
      }
      this.updateViewerControls();
    }

    toggleViewerPlayback() {
      const video = this.activeViewerVideo();
      if (!video) return;
      if (video.paused) video.play()?.catch(() => {});
      else video.pause();
      this.updateViewerControls();
    }

    toggleViewerSound() {
      const video = this.activeViewerVideo();
      if (!video) return;
      this.viewerMuted = !this.viewerMuted;
      video.muted = this.viewerMuted;
      this.updateViewerControls();
    }

    updateViewerControls() {
      const video = this.activeViewerVideo();
      if (!video) return;
      this.viewerPrevious.disabled = this.activeVideoIndex === 0;
      this.viewerNext.disabled = this.activeVideoIndex === this.viewerSlides().length - 1;
      this.viewerPlay.classList.toggle('is-paused', video.paused);
      this.viewerPlay.setAttribute('aria-label', video.paused ? 'Play video' : 'Pause video');
      this.viewerSound.classList.toggle('is-unmuted', !this.viewerMuted);
      this.viewerSound.setAttribute('aria-label', this.viewerMuted ? 'Turn sound on' : 'Turn sound off');
    }

    scrollByCard(direction) {
      const firstCard = this.cards[0];
      if (!firstCard || !this.viewport) return;
      const gap = Number.parseFloat(getComputedStyle(this.track).gap) || 0;
      this.viewport.scrollBy({ left: direction * (firstCard.getBoundingClientRect().width + gap), behavior: 'smooth' });
    }

    updateButtons() {
      if (!this.viewport) return;
      const maxScroll = this.viewport.scrollWidth - this.viewport.clientWidth;
      if (this.previousButton) this.previousButton.disabled = this.viewport.scrollLeft <= 2;
      if (this.nextButton) this.nextButton.disabled = this.viewport.scrollLeft >= maxScroll - 2 || maxScroll <= 2;
    }

    handleBlockSelect(event) {
      const card = event.target.closest?.('[data-sooler-ugc-card]');
      if (!card || !this.contains(card)) return;
      card.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }

    handleKeydown(event) {
      if (!this.viewer || this.viewer.hidden) return;
      if (event.key === 'Escape') this.closeViewer();
      if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') this.activateViewerVideo(this.activeVideoIndex - 1);
      if (event.key === 'ArrowDown' || event.key === 'ArrowRight') this.activateViewerVideo(this.activeVideoIndex + 1);
    }
  });
}
